import {Box, Button, Typography} from '@viptech/react-components'
import {useSnackbar} from 'notistack'
import {useState} from 'react'
import {DrawBarrierWithSvg} from 'src/common/components/draw-lines-only/DrawBarrierWithSvg'
import useSnapshot from 'src/hooks/api/camera/useSnapshot'
import useInterestArea from 'src/hooks/api/interest-areas/useInterestAreas'
import NoImage from 'src/images/noImage.svg'
import {useWindowSize} from 'usehooks-ts'
import Dropdown from '../../../../../../../../components/dropdown/Dropdown'
import {DropdownItem} from '../../../../../../../../components/dropdown/DropdownProps'
import {CameraHasObjects, Coordinate} from '../../../../../../../../services/api'
import handleErrorWithSnackbar from '../../../../../../../../utilities/handleErrorWithSnackbar'
import {useCameraConfigContext} from '../../../../../../context/CameraConfigContext'
import {StepperModalActionsButtonContainer, StepperModalBarrierContainer} from '../../../styles'
import Card from '../../common/components/Card'
import Separation from '../../common/components/Separation'
import {ChosenObject} from '../../StepperModal'
import {firstStepResourceIdSet, skipThirdStep} from '../Constants'
import {BarrierPreviewContainer, ChooseBarrierContent, ContainerChooseBarrier} from './ChooseBarrierStepBase'

type ChooseBarrierStepProps = {
  nextFunction: (index: number, resume: string, advanceTo?: number) => void
  returnFunction: (index: number) => void
  setChosen: (choices: Partial<ChosenObject>) => void
  chosen: ChosenObject | null
  detectionDisableLineCrossing: boolean
  hasLineCrossingPermission: boolean
  registeredObjects: CameraHasObjects[] | undefined
}

export type BarrierChosenType = {
  id: string
  cameraConfigurationId: number
  label: string
  name: string
  coordinates: Array<Coordinate>
  type: string
}

const ChooseBarrierStep = (props: ChooseBarrierStepProps) => {
  const {nextFunction, returnFunction, setChosen, chosen, registeredObjects} = props
  const {enqueueSnackbar} = useSnackbar()
  const {camera} = useCameraConfigContext()

  const barrierOptions = ['Cruzamento de linha', 'Detecção em uma área']
  if (props.detectionDisableLineCrossing || !props.hasLineCrossingPermission) barrierOptions.shift()
  const [barrierOptionsSelectedCard, setBarrierOptionsSelectedCard] = useState('')
  const showSeletectedBarrier = barrierOptionsSelectedCard === 'Cruzamento de linha' ? 'linha' : 'área'

  const [chosenAreaOrLine, setChosenAreaOrLine] = useState<BarrierChosenType>()
  const [listingOfAllAreasOrLines, setListingOfAllAreasOrLines] = useState<BarrierChosenType[]>([])

  const windowSize = useWindowSize()

  const {data: barriers} = useInterestArea({
    cameraId: camera?.id ?? -1,
    onError: (error) => handleErrorWithSnackbar(enqueueSnackbar, error, 'Erro ao carregar áreas'),
    enabled: !!camera?.id,
  })

  const {data: snapshot} = useSnapshot({
    cameraId: camera?.id,
    enabled: !!camera?.id,
  })

  const filterBarriers = (barrierOptionsSelectedCard: string) => {
    setBarrierOptionsSelectedCard(barrierOptionsSelectedCard)

    const barrierSelected = barrierOptionsSelectedCard === 'Cruzamento de linha' ? 'line-crossing' : 'interest-area'

    const filtered: BarrierChosenType[] =
      barriers
        ?.filter((it) => it.type === barrierSelected)
        .map((it) => {
          const points = it.coordinates.map((point) => {
            return {
              ...point,
              x: point.x,
              y: point.y,
            }
          })
          return {
            ...it,
            label: it.name,
            id: String(it.id),
            coordinates: points,
          }
        }) ?? []

    setListingOfAllAreasOrLines(filtered)
  }

  const handleChosenBarrier = (e: DropdownItem) => {
    const registeredAreaIds = registeredObjects?.map((it) => it.interestArea?.id)
    const registeredObjectIds = registeredObjects?.map((it) => it.objectType.id)
    const alreadyHaveThisResourceRegistered =
      registeredObjectIds?.includes(chosen?.objectTypeId!) && firstStepResourceIdSet.includes(chosen?.detectionTypeChosen!)

    const alreadyHaveThisAreaRegistered = registeredAreaIds?.includes(+e.id)

    const objectRegistered = registeredObjects?.find((it) => it.objectTypeId === chosen?.objectTypeId!)

    if (alreadyHaveThisAreaRegistered && alreadyHaveThisResourceRegistered && objectRegistered?.isObjectPermanency === chosen?.isObjectPermanency)
      return enqueueSnackbar('Já existe uma detecção desse objeto nessa área, por favor selecione outra', {variant: 'warning'})

    setChosenAreaOrLine(listingOfAllAreasOrLines.find((it) => it.id === e.id))

    const type = barrierOptionsSelectedCard === 'Cruzamento de linha' ? 'line-crossing' : 'interest-area'
    setChosen({
      type,
      interestAreaId: Number(e.id),
    })
  }

  const handleNextFunction = () => {
    if (skipThirdStep.includes(chosen?.detectionTypeChosen!)) return nextFunction(2, barrierOptionsSelectedCard, 3)
    nextFunction(2, barrierOptionsSelectedCard)
  }

  const previewWidth = windowSize.height < 720 ? 320 : 380
  const previewHeight = windowSize.height < 720 ? 180 : 220

  return (
    <ContainerChooseBarrier>
      <ChooseBarrierContent>
        <StepperModalBarrierContainer>
          <Box flex={1 / 2} display='flex' direction='column' rowGap='24px' padding='10px 40px' minHeight={windowSize.height < 720 ? '200px' : '364px'}>
            <Box>
              <Typography variant='span' size='16px' style={{fontWeight: 500}}>
                Qual tipo de barreira você deseja configurar?
              </Typography>
              <Typography variant='p' size='12px' color='#8E8E8E'>
                Escolha abaixo o tipo de barreira para configuração
              </Typography>
            </Box>

            <Box display='flex' direction='column' flexWrap='wrap' rowGap='16px'>
              {barrierOptions.map((it) => {
                return (
                  <Card
                    dispatchTo={it}
                    selected={barrierOptionsSelectedCard === it}
                    onClick={() => filterBarriers(it)}
                    initialState={!barrierOptionsSelectedCard}
                  />
                )
              })}
            </Box>
          </Box>

          <Box className='separation'>
            <Separation />
          </Box>

          {barrierOptionsSelectedCard && (
            <Box display='flex' direction='row' flex={1} columnGap='18px' justifyContent='center'>
              <Box>
                <Typography variant='span' size='16px' style={{fontWeight: 500}}>
                  Qual {showSeletectedBarrier} você deseja configurar?
                </Typography>
                <Typography variant='p' size='12px' color='#8E8E8E'>
                  Escolha abaixo qual {showSeletectedBarrier} que você deseja configurar
                </Typography>

                <Box paddingTop={windowSize.height < 720 ? '10px' : '20px'}>
                  <Dropdown
                    height='40px'
                    fontSize='12px'
                    selected={chosenAreaOrLine}
                    placeholder={`Selecione uma das ${showSeletectedBarrier}s cadastradas`}
                    items={listingOfAllAreasOrLines}
                    onChangeSelected={(e) => handleChosenBarrier(e)}
                  />

                  <BarrierPreviewContainer>
                    {snapshot && (
                      <DrawBarrierWithSvg imageSrc={snapshot.time.base64} points={chosenAreaOrLine?.coordinates} width={previewWidth} height={previewHeight} />
                    )}
                    {!snapshot && <img src={NoImage} alt='sem imagem' width={'250px'} />}
                  </BarrierPreviewContainer>
                </Box>
              </Box>
            </Box>
          )}
        </StepperModalBarrierContainer>
      </ChooseBarrierContent>
      <StepperModalActionsButtonContainer>
        <Button fontSize='12px' height='40px' width='150px' color='#8E8E8E' variant='outlined' onClick={() => returnFunction(0)}>
          Voltar
        </Button>
        <Button fontSize='12px' height='40px' disabledTextColor='#8E8E8E' width='150px' disabled={!chosenAreaOrLine} onClick={() => handleNextFunction()}>
          Próximo
        </Button>
      </StepperModalActionsButtonContainer>
    </ContainerChooseBarrier>
  )
}

export default ChooseBarrierStep
